import styles from "./admin.module.scss";
import { SearchInput } from "../../shared/components/searchInput/searchInput";
import {
  useEffect,
  useMemo,
  useState
} from "react";
import addIcon from "../../shared/images/add.svg"
import { ReactComponent as ArrowSvg } from "../../shared/images/arrow2.svg"
import { useTranslation } from "react-i18next";
import { UserTableItem } from "../../components/userTableItem/userTableItem";
import { UserModal } from "../../components/userModal/userModal";
import { useGetAllUsersQuery } from "../../api/services/user.service";
import {
  useDispatch,
  useSelector
} from "react-redux";
import {
  AppDispatch,
  RootState
} from "../../store";
import { setShowSpinner } from "../../store/slices/mainSlice";
import { User } from "../../types/user.types";
import {
  useGetAllProjectsQuery,
  useGetUserProjectsQuery
} from "../../api/services/project.service";
import { useGetAllRolesQuery } from "../../api/services/role.service";
import { Checkbox } from "../../shared/components/checkbox/checkbox";
import { Link } from "react-router-dom";
import { UserField } from "../../constants/userField";
import {
  changeSortAdmin,
  setShowDelete
} from "../../store/slices/adminSlice";
import {
  filteringUser,
  sortingUsers
} from "../../utils/users.util";
import {
  Dropdown,
  DropdownItem
} from "../../shared/components/dropdown/dropdown";
import {
  number,
  object,
} from "yup";
import {useForm, useWatch} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {AddExistingUserModal} from "../../components/addExistingUserModal/addExistingUserModal";

export const Admin = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();

  const { sortAdminTable, showDelete } = useSelector((state: RootState) => state.adminReducer);

  const [searchValue, setSearchValue] = useState<string>("");
  const [filteredUsers, setFilteredUsers] = useState<Array<User>>([]);

  const [showUserModal, setShowUserModal] = useState<boolean>(false);
  const [showAddExistingUserModal, setShowAddExistingUserModal] = useState<boolean>(false);

  const { data: projects } = useGetAllProjectsQuery("", { refetchOnMountOrArgChange: true });
  const { data: roles } = useGetAllRolesQuery("", { refetchOnMountOrArgChange: true });
  const { data: users, refetch: refetchUsers, isFetching: isFetchingUsers } = useGetAllUsersQuery("", { refetchOnMountOrArgChange: true });
  const { data: userProjects } = useGetUserProjectsQuery("", { refetchOnMountOrArgChange: true });

  const initialSchema = object({
    projectId: number().required(t("common.notValid"))
  });

  const { control } = useForm({
    resolver: yupResolver(initialSchema),
    defaultValues: {
      projectId: 0,
    },
    mode: "onChange"
  });
  const projectIdWatch = useWatch({ control, name: "projectId" });


  useEffect(() => {
    if (isFetchingUsers) {
      dispatch(setShowSpinner(true));
    } else {
      dispatch(setShowSpinner(false));
    }
  }, [isFetchingUsers]);

  useEffect(() => {
    const newFilteredUsers = filteringUser(showDelete, searchValue, projectIdWatch, users, projects, roles);
    const newSortingUsers = sortingUsers(sortAdminTable.field, sortAdminTable.format, newFilteredUsers, projects, roles);
    setFilteredUsers(newSortingUsers);
  }, [searchValue, users, projects, showDelete, sortAdminTable.format, sortAdminTable.field, projectIdWatch]);

  const onChangeSearch = (value: string): void => {
    setSearchValue(value);
  }

  const onClickAddUser = (): void => {
    setShowUserModal(true);
  }

  const onClickAddExitingUser = (): void => {
    setShowAddExistingUserModal(true);
  }

  const onCloseUserModal = (isSave: boolean): void => {
    if (isSave) {
      refetchUsers();
    }
    setShowUserModal(false);
  }

  const onCloseAddExistingUser = (isSave: boolean): void => {
    if (isSave) {
      refetchUsers();
    }
    setShowAddExistingUserModal(false);
  }

  const onClickSort = (field: UserField): void => {
    dispatch(changeSortAdmin(field));
  }

  const projectDropdownItems: Array<DropdownItem> = useMemo(() => {
    return [{
      id: 0,
      name: t("admin.allProjects"),
      title: t("admin.allProjects"),
    }, ...(userProjects?.map(project => ({
      id: project.id,
      name: project.name,
      title: project.name
    })) || []) ];
  }, [userProjects]);

  return (
    <>
      <div className={styles.container}>
        <div className={styles.headerLine}></div>
        <div className={styles.header}>
          <div className={styles.leftBlock}>
            <Link to={"/"} className={styles.back}>
              <div>
                <ArrowSvg />
              </div>
              {t("admin.back")}
            </Link>
            <div className={styles.search}>
              <SearchInput value={searchValue} onChange={onChangeSearch}/>
            </div>
            <div className={styles.projectsDropdown}>
              <Dropdown items={projectDropdownItems}
                        name={"projectId"}
                        control={control}/>
            </div>
          </div>

          <div className={styles.rightBlock}>
            <div className={styles.showDeleted} onClick={() => dispatch(setShowDelete(!showDelete))}>
              <Checkbox isChecked={showDelete}/>
              {t("admin.showDeleted")}
            </div>
            <div className={styles.addUser}
                 onClick={onClickAddExitingUser}>
              <img src={addIcon} alt=""/>
              {t("admin.addExistingUser")}
            </div>
            <div className={styles.addUser}
                 onClick={onClickAddUser}>
              <img src={addIcon} alt=""/>
              {t("admin.addNewUser")}
            </div>
          </div>

        </div>
        <div className={styles.body}>
          <div className={styles.table}>
            <div className={styles.tableHeader}>
              {Object.values(UserField).map(((adminField, index) => {
                let result;
                let needSort = true;
                if (adminField === UserField.login) {
                  result = (
                    <div className={styles.login}>
                      {t("login.login")}
                    </div>
                  )
                } else if (adminField === UserField.passwordExpirationDate) {
                  result = (<div className={styles.expirationDate}>{t("admin.passwordExpirationDate")}</div>)
                } else if (adminField === UserField.deleted) {
                  needSort = showDelete
                  result = (<>{showDelete && t("admin.deleted")}</>)
                } else {
                  result = (<>{t(`admin.${adminField}`)}</>)
                }
                return (
                  <div className={`${styles.item} ${needSort ? styles.action : ""}`}
                       onClick={() => needSort && onClickSort(adminField)}
                       key={index}>
                    {result}
                    { needSort && (
                      <div className={styles.arrow}>
                        {sortAdminTable.field === adminField &&
                          <ArrowSvg className={sortAdminTable.format === "ASC" ? styles.asc : ""}/>}
                      </div>
                    ) }
                  </div>
                )
              }))}
              <div></div>
            </div>
            <div className={styles.tableBody}>
              {filteredUsers.map((user, index) =>
                (<UserTableItem key={index} user={user} refetch={refetchUsers}/>)
              )}
            </div>
          </div>
        </div>
      </div>
      {showUserModal && <UserModal onClose={onCloseUserModal}/>}
      {showAddExistingUserModal && <AddExistingUserModal onClose={onCloseAddExistingUser}/>}
    </>
  )
}
